import React, { useEffect, useState } from 'react';
import { Zap, Check, ArrowRight, ExternalLink } from 'lucide-react';
import api from '../services/api';
import ContactModal from '../components/ContactModal';

const Billing: React.FC = () => {
    const [stats, setStats] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const data = await api.getDashboardStats();
            setStats(data);
        } catch (error) {
            console.error('Failed to load billing data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpgrade = () => {
        setIsContactModalOpen(true);
    };

    const handleManage = async () => {
        try {
            const { url } = await api.createPortalSession();
            if (url) window.open(url, '_blank');
        } catch (error) {
            console.error('Portal failed:', error);
            alert('Error al abrir el portal de gestión');
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white">Billing</h1>
                    <p className="text-slate-400 mt-1">Gestiona tu suscripción y monitoriza tu consumo.</p>
                </div>
                <button
                    onClick={handleManage}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors border border-slate-700"
                >
                    <ExternalLink className="w-4 h-4" />
                    Portal de Gestión
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Current Plan Info */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-surface-dark border border-slate-800 rounded-2xl p-6 relative overflow-hidden">
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-2 bg-primary/10 rounded-lg">
                                    <Zap className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Plan Actual</h3>
                                    <p className="text-2xl font-bold text-white uppercase">{stats?.plan || 'Free'}</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-slate-400">Consumo Mensual (Generaciones)</span>
                                        <span className="text-white font-medium">{stats?.total_generations || 0} / {stats?.max_generations || 50}</span>
                                    </div>
                                    <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-primary transition-all duration-1000"
                                            style={{ width: `${Math.min(((stats?.total_generations || 0) / (stats?.max_generations || 50)) * 100, 100)}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Background Decoration */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -mr-32 -mt-32 blur-3xl" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-surface-dark border border-slate-800 rounded-xl p-6">
                            <h4 className="text-white font-semibold mb-4">Límites Disponibles</h4>
                            <ul className="space-y-3">
                                <li className="flex items-center gap-2 text-sm text-slate-400">
                                    <Check className="w-4 h-4 text-green-500" />
                                    <span>{stats?.max_generations || 50} Generaciones / mes</span>
                                </li>
                                <li className="flex items-center gap-2 text-sm text-slate-400">
                                    <Check className="w-4 h-4 text-green-500" />
                                    <span>{stats?.max_projects || 1} Proyecto{(stats?.max_projects || 1) > 1 ? 's' : ''} Activo{(stats?.max_projects || 1) > 1 ? 's' : ''}</span>
                                </li>
                                <li className="flex items-center gap-2 text-sm text-slate-400">
                                    <Check className="w-4 h-4 text-green-500" />
                                    <span>Soporte {stats?.plan === 'pro' ? 'Prioritario' : 'Estándar'}</span>
                                </li>
                            </ul>
                        </div>
                        <div className="bg-surface-dark border border-slate-800 rounded-xl p-6">
                            <h4 className="text-white font-semibold mb-4">Próxima Factura</h4>
                            <p className="text-3xl font-bold text-white">$0.00</p>
                            <p className="text-sm text-slate-500 mt-1">Siguiente ciclo: 1 Feb 2026</p>
                        </div>
                    </div>
                </div>

                {/* Upgrade Card - Only show if not PRO */}
                {stats?.plan !== 'pro' ? (
                    <div className="bg-gradient-to-b from-primary/20 to-surface-dark border border-primary/30 rounded-2xl p-8 flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-6">
                            <Zap className="w-8 h-8 text-primary" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Pásate a PRO</h3>
                        <p className="text-slate-400 mb-8 font-medium">Libera todo el potencial de tu equipo de QA con límites ampliados y funciones avanzadas.</p>

                        <ul className="w-full space-y-4 mb-8 text-left">
                            <li className="flex items-center gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-primary shrink-0" />
                                <span>500 Generaciones / mes</span>
                            </li>
                            <li className="flex items-center gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-primary shrink-0" />
                                <span>Proyectos Ilimitados</span>
                            </li>
                            <li className="flex items-center gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-primary shrink-0" />
                                <span>Integración Jira & Confluence</span>
                            </li>
                            <li className="flex items-center gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-primary shrink-0" />
                                <span>Soporte Prioritario</span>
                            </li>
                        </ul>

                        <button
                            onClick={handleUpgrade}
                            className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-primary hover:bg-primary-dark text-white font-bold rounded-xl transition-all h-14 group"
                        >
                            Obtener Pro Ahora
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                ) : (
                    <div className="bg-gradient-to-b from-green-500/10 to-surface-dark border border-green-500/30 rounded-2xl p-8 flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                            <Check className="w-8 h-8 text-green-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Suscripción Activa</h3>
                        <p className="text-slate-400 mb-8">Gracias por confiar en QA SmartCases. Tu equipo tiene acceso total a todas las herramientas.</p>

                        <div className="w-full p-4 bg-green-500/5 rounded-xl border border-green-500/10 mb-8">
                            <p className="text-green-500 font-semibold">Plan PRO habilitado</p>
                            <p className="text-xs text-slate-500 mt-1">Tu cuenta está al día.</p>
                        </div>

                        <ul className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
                            <li className="flex items-center gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-green-500 shrink-0" />
                                <span>{stats?.max_generations || 500} Generaciones / mes</span>
                            </li>
                            <li className="flex items-center gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-green-500 shrink-0" />
                                <span>{stats?.max_projects === 100 ? 'Proyectos Ilimitados' : `${stats?.max_projects} Proyectos`}</span>
                            </li>
                            <li className="flex items-center gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-green-500 shrink-0" />
                                <span>Conector Jira/Confluence OK</span>
                            </li>
                            <li className="flex items-center gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-green-500 shrink-0" />
                                <span>Análisis Predictivo de Riesgos</span>
                            </li>
                            <li className="flex items-center gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-green-500 shrink-0" />
                                <span>Acceso Ilimitado a IA</span>
                            </li>
                            <li className="flex items-center gap-3 text-slate-300">
                                <Check className="w-5 h-5 text-green-500 shrink-0" />
                                <span>Soporte VIP 24/7</span>
                            </li>
                        </ul>
                    </div>
                )}
            </div>
            <ContactModal
                isOpen={isContactModalOpen}
                onClose={() => setIsContactModalOpen(false)}
                source="billing_upgrade_pro"
            />
        </div>
    );
};

export default Billing;
